import type { CurrentUserDto } from "@gladis/shared";
import { ActionMenu, Avatar } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { apiClient } from "../../lib/api.js";
import { useAuth } from "../../lib/auth-context.js";

/**
 * The member's own account, behind their own face.
 *
 * ==========================================================================================
 * WHAT MOVED HERE, AND WHY THE RAIL IS THE WRONG PLACE FOR IT
 * ==========================================================================================
 *
 * `Sign out` was the last entry in the navigation rail, pinned below a spacer with the Settings link.
 * `side-nav.tsx` argued that group carefully — "appearance, then preferences, then leaving" — and the
 * argument was about a rail that had nowhere else to put them.
 *
 * They are not destinations. The rail is a list of places in the application; signing out is not a place,
 * and neither is changing the theme. Moving them into the chrome at the top right leaves the rail as one
 * kind of thing — sections you navigate to — which is what its own comment says it is for.
 *
 * ==========================================================================================
 * THE TRIGGER IS THE AVATAR, AND THAT IS THE POINT
 * ==========================================================================================
 *
 * `ActionMenu` gained a `trigger` slot for this. Its default three dots say "there are actions here",
 * which is right on a table row where the row is the subject; here the member IS the subject. It is also
 * the one place in the chrome a member sees themselves — 020 put an avatar on the dashboard for that
 * reason and 019 removed it, because a bare identity line is neither a balance, a flow, nor context
 * about the data. This is where it belongs instead.
 *
 * ==========================================================================================
 * TWO ITEMS, TWO PAGES
 * ==========================================================================================
 *
 * `/settings` is a single page with two sections — `HouseholdSettings`, then `ProfileSettings` after a
 * divider — and it stays one page. The two items carry a hash so each lands on the half it names, which
 * is a real difference to the reader without inventing two routes for one screen.
 *
 * The order is deliberate and matches the page: household, then profile, then the one that ends the
 * session. `destructive` is not used — signing out removes nothing, and spending the danger colour on it
 * would leave nothing to distinguish "delete my account", which is two clicks further on.
 */
export function AccountMenu({ me }: { me: CurrentUserDto }): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  const { clear } = useAuth();

  /*
   * MOVED HERE FROM `side-nav.tsx` RATHER THAN COPIED. The rail no longer offers signing out, so this is
   * the only implementation and there is nothing to drift from.
   *
   * Server first: the cookie is cleared there, and clearing locally before that would leave a live
   * refresh token behind if the request failed. The hard navigation is deliberate — `router.navigate`
   * would keep every cached query alive across a session boundary.
   */
  async function signOut(): Promise<void> {
    await apiClient.post("/auth/sign-out");
    clear();
    window.location.assign("/sign-in");
  }

  return (
    <ActionMenu
      // Named for the member, not "Account": a screen-reader user hears whose menu it is, which is the
      // same reason `ActionMenu`'s own documentation gives for "Actions for Damien — salary".
      label={`Account and settings for ${me.user.displayName}`}
      trigger={
        <>
          <Avatar src={me.user.avatarUrl} name={me.user.displayName} size="sm" />
          {/*
            THE NAME BESIDE THE FACE. Two people in one household is the case this is for: initials on a
            32px circle are a guess, and a household of Damien and Daniel would show "D" twice.

            HIDDEN BELOW `sm`, where the bar also holds the household switcher and the theme picker and
            there is no room for three things and a name. The avatar survives at every width, so the
            control never disappears — it only stops saying whose it is, which the `aria-label` still does.

            `aria-hidden` because the button's own name already reads "Account and settings for Ada": left
            visible to assistive technology this would be announced twice, once as content and once as the
            name that overrides it.
          */}
          <span aria-hidden="true" className="hidden text-sm text-ink sm:inline">
            {me.user.displayName}
          </span>
        </>
      }
      // Replaces the default 32px square: this is a row of two things now, and a pill rather than a
      // circle. The focus ring is re-declared here rather than merged, because two class strings both
      // setting a radius resolve to whichever Tailwind emitted last.
      triggerClassName="inline-flex cursor-pointer items-center gap-2 rounded-full py-1 pr-2 pl-1 outline-none transition-colors hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50"
      items={[
        {
          label: t("nav.householdSettings"),
          onSelect: () => void navigate({ to: "/settings/household" }),
        },
        {
          label: t("nav.profileSettings"),
          onSelect: () => void navigate({ to: "/settings/profile" }),
        },
        { label: t("nav.signOut"), onSelect: () => void signOut() },
      ]}
    />
  );
}
